import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { acceptOffer, lineItemTotal, type OfferLineItem } from '@/lib/offers';
import AsyncStorage from '@react-native-async-storage/async-storage';

const wallpaper = require('../../../assets/wallpaper.jpg');
const logo = require('../../../assets/logo.png');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.88)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

const PENDING_OFFER_KEY = 'pending_offer_id';

type OfferPreview = {
  id: string;
  title: string;
  description: string | null;
  cost: number | null;
  currency: string | null;
  delivery_days: number | null;
  offer_kind: string | null;
  line_items: OfferLineItem[] | null;
  creator_name: string;
  creator_avatar: string | null;
  business_name: string | null;
  business_logo_url: string | null;
  professional_terms: string | null;
};

export default function OfferInviteScreen() {
  const { t } = useTranslation();
  const { offer: offerId } = useLocalSearchParams<{ offer?: string }>();
  const { session } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';

  const [preview, setPreview] = useState<OfferPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  async function submitCode() {
    if (!offerId || code.trim().length !== 6) return;
    setVerifying(true);
    setCodeError(null);
    try {
      const r = await fetch(`${supabaseUrl}/functions/v1/offer-preview/${offerId}?code=${encodeURIComponent(code.trim())}`);
      const data = await r.json();
      if (data.error === 'invalid_code') {
        setCodeError(t('invite.offer_codeInvalid'));
        return;
      }
      if (data.error === 'too_many_attempts') {
        setCodeError(t('invite.offer_codeTooManyAttempts'));
        return;
      }
      if (data.error) {
        setError(t('invite.offer_notFound'));
        return;
      }

      if (session) {
        setLoading(true);
        setAccepting(true);
        try {
          const agreementId = await acceptOffer(offerId);
          router.replace({ pathname: '/(app)/agreements/[id]', params: { id: agreementId } });
        } catch {
          setError(t('invite.offer_acceptError'));
        }
        return;
      }
      await AsyncStorage.setItem(PENDING_OFFER_KEY, offerId);
      setPreview(data as OfferPreview);
    } catch {
      setError(t('invite.offer_loadError'));
    } finally {
      setVerifying(false);
    }
  }

  function goRegister() { router.replace('/(auth)/register'); }
  function goLogin() { router.replace('/(auth)/login'); }

  return (
    <ImageBackground source={wallpaper} style={styles.flex} resizeMode="cover">
      <View style={styles.overlay} />
      <View
        style={[
          styles.container,
          { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
        ]}
      >
        <View style={styles.logoWrap}>
          <Image source={logo} style={styles.logo} resizeMode="contain" />
        </View>

        {loading || accepting ? (
          <View style={[styles.card, styles.center]}>
            <ActivityIndicator color={GOLD} size="large" />
            {accepting && <Text style={styles.loadingText}>{t('invite.offer_accepting')}</Text>}
          </View>
        ) : error ? (
          <View style={styles.card}>
            <Text style={styles.errorText}>{error}</Text>
            <Button title={t('auth.action.login')} onPress={goLogin} />
            <Button title={t('auth.action.register')} variant="secondary" onPress={goRegister} />
          </View>
        ) : !preview ? (
          <View style={styles.card}>
            <View style={styles.inviteHeader}>
              <Ionicons name="briefcase-outline" size={28} color={GOLD} />
              <Text style={styles.inviteLabel}>{t('invite.offer_header')}</Text>
            </View>
            <Text style={styles.inviteText}>{t('invite.offer_codePrompt')}</Text>
            <TextInput
              style={styles.codeInput}
              value={code}
              onChangeText={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('invite.offer_codePlaceholder')}
              placeholderTextColor={TEXT_MUTED}
              keyboardType="number-pad"
              maxLength={6}
              autoFocus
            />
            {!!codeError && <Text style={styles.errorText}>{codeError}</Text>}
            <Button
              title={t('invite.offer_codeSubmit')}
              onPress={submitCode}
              disabled={code.length !== 6 || verifying}
              loading={verifying}
            />
          </View>
        ) : preview ? (
          <View style={styles.card}>
            <View style={styles.inviteHeader}>
              <Ionicons name="briefcase-outline" size={28} color={GOLD} />
              <Text style={styles.inviteLabel}>{t('invite.offer_header')}</Text>
            </View>

            <View style={styles.creatorRow}>
              {preview.business_name && preview.business_logo_url ? (
                <Image source={{ uri: preview.business_logo_url }} style={styles.avatar} />
              ) : preview.creator_avatar ? (
                <Image source={{ uri: preview.creator_avatar }} style={styles.avatar} />
              ) : (
                <View style={styles.avatarFallback}>
                  <Text style={styles.avatarInitial}>
                    {(preview.business_name || preview.creator_name).charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <View style={styles.creatorTextWrap}>
                <Text style={styles.creatorName}>{preview.business_name || preview.creator_name}</Text>
                {!!preview.business_name && (
                  <Text style={styles.creatorSubName}>{preview.creator_name}</Text>
                )}
              </View>
            </View>

            <View style={styles.offerCard}>
              <Text style={styles.offerTitle}>{preview.title}</Text>
              {!!preview.description && (
                <Text style={styles.offerDesc} numberOfLines={4}>{preview.description}</Text>
              )}

              {preview.offer_kind === 'goods' && !!preview.line_items?.length && (
                <View style={styles.lineItemsBox}>
                  {preview.line_items.map((it, i) => (
                    <View key={i} style={styles.lineItemRow}>
                      <Ionicons name="cube-outline" size={16} color={GOLD} />
                      <Text style={styles.lineItemText} numberOfLines={2}>{it.description} ×{it.quantity}</Text>
                      <Text style={styles.detailText}>{lineItemTotal(it).toFixed(2)} €</Text>
                    </View>
                  ))}
                </View>
              )}

              <View style={styles.offerDetails}>
                {preview.cost != null && (
                  <View style={styles.detailItem}>
                    <Ionicons name="cash-outline" size={16} color={GOLD} />
                    <Text style={styles.detailText}>{preview.cost.toFixed(2)} €</Text>
                  </View>
                )}
                {preview.delivery_days != null && (
                  <View style={styles.detailItem}>
                    <Ionicons name="time-outline" size={16} color={GOLD} />
                    <Text style={styles.detailText}>{t('offers.deliveryDays', { count: preview.delivery_days })}</Text>
                  </View>
                )}
              </View>
            </View>

            {!!preview.professional_terms && (
              <View style={styles.termsBox}>
                <Text style={styles.termsLabel}>
                  {t('invite.professionalTerms', { name: preview.business_name || preview.creator_name })}
                </Text>
                <Text style={styles.termsText}>{preview.professional_terms}</Text>
              </View>
            )}

            <Text style={styles.inviteText}>
              {t('invite.offer_acceptPrompt')}
            </Text>

            <Button title={t('invite.action_registerAndAccept')} icon="create-outline" onPress={goRegister} />
            <View style={styles.divider}>
              <Text style={styles.dividerText}>{t('invite.have_account')}</Text>
            </View>
            <Button title={t('auth.action.login')} variant="secondary" onPress={goLogin} />
          </View>
        ) : (
          <View style={styles.card}>
            <Text style={styles.errorText}>{t('invite.offer_notFound')}</Text>
            <Button title={t('auth.action.login')} onPress={goLogin} />
            <Button title={t('auth.action.register')} variant="secondary" onPress={goRegister} />
          </View>
        )}
      </View>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  overlay: {
    ...require('react-native').StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13,27,42,0.68)',
  },
  container: {
    // Κεντραρισμένη κάρτα σε μεγάλες οθόνες αντί για τέντωμα.
    width: '100%',
    maxWidth: contentMaxWidth.form,
    alignSelf: 'center',
    flex: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
  },
  logoWrap: { alignItems: 'center' },
  logo: { width: 120, height: 120 },
  card: {
    backgroundColor: CARD_BG, borderRadius: radius.xl,
    borderWidth: 1, borderColor: CARD_BORDER, padding: spacing.lg, gap: spacing.md,
  },
  codeInput: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: radius.md,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
    color: TEXT_WHITE, fontSize: fontSize.xl, fontWeight: '700',
    letterSpacing: 8, textAlign: 'center', paddingVertical: spacing.md,
  },
  center: { alignItems: 'center', justifyContent: 'center', minHeight: 180 },
  loadingText: { color: TEXT_MUTED, fontSize: fontSize.sm, marginTop: spacing.sm },
  inviteHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  inviteLabel: { color: GOLD, fontSize: fontSize.lg, fontWeight: '700' },
  creatorRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  avatar: { width: 44, height: 44, borderRadius: 22 },
  avatarFallback: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(201,168,67,0.2)', alignItems: 'center', justifyContent: 'center',
  },
  avatarInitial: { color: GOLD, fontSize: fontSize.xl, fontWeight: '700' },
  creatorTextWrap: { flex: 1, gap: 2 },
  creatorName: { color: TEXT_WHITE, fontSize: fontSize.md, fontWeight: '600' },
  creatorSubName: { color: TEXT_MUTED, fontSize: fontSize.xs },
  termsBox: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: radius.md,
    padding: spacing.md, gap: spacing.xs,
    borderWidth: 1, borderColor: 'rgba(201,168,67,0.3)',
  },
  termsLabel: { color: GOLD, fontSize: fontSize.xs, fontWeight: '700' },
  termsText: { color: TEXT_MUTED, fontSize: fontSize.sm, lineHeight: 19 },
  offerCard: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: radius.md,
    padding: spacing.md, gap: spacing.sm,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
  },
  offerTitle: { color: TEXT_WHITE, fontSize: fontSize.md, fontWeight: '700' },
  offerDesc: { color: TEXT_MUTED, fontSize: fontSize.sm, lineHeight: 19 },
  lineItemsBox: {
    gap: spacing.xs, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.10)',
    paddingTop: spacing.sm,
  },
  lineItemRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  lineItemText: { flex: 1, color: TEXT_WHITE, fontSize: fontSize.sm },
  offerDetails: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, marginTop: spacing.xs },
  detailItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  detailText: { color: GOLD, fontSize: fontSize.sm, fontWeight: '600' },
  inviteText: { color: TEXT_MUTED, fontSize: fontSize.md, lineHeight: 22, textAlign: 'center' },
  divider: { alignItems: 'center' },
  dividerText: { color: TEXT_MUTED, fontSize: fontSize.sm },
  errorText: { color: '#F87171', fontSize: fontSize.md, textAlign: 'center' },
});
